import { constants } from '@redbubble/design-system';
import { serializeExperiments, } from '@redbubble/next-experiments';
import logger from '@redbubble/next-logger';
import { storage } from '@redbubble/persistent-storage';
import { format, isValid, parseISO } from 'date-fns';
import { AnalyticsContextTypes, AnalyticsItemsListIDNameMap, AnalyticsStorageKeys, } from './constants';
export var formatDate = function (date) {
    var parsedDate = parseISO(date);
    if (date && isValid(parsedDate)) {
        return format(parsedDate, 'yyyyMMdd');
    }
    return date;
};
var MEMBER = 'Member';
var VISITOR = 'Visitor';
var NEW_VISITOR = 'New Visitor';
// biome-ignore lint/complexity/useLiteralKeys: useLiteralKeys
var isVisitor = function (cookies) { return !!cookies['_ga']; };
var isMember = function (userInfo) { return userInfo.isLoggedIn; };
var getVisitorType = function (cookies, userInfo) {
    if (isMember(userInfo)) {
        return MEMBER;
    }
    if (isVisitor(cookies)) {
        return VISITOR;
    }
    return NEW_VISITOR;
};
var GaUserTypes = {
    getVisitorType: getVisitorType,
    Member: MEMBER,
    Visitor: VISITOR,
    NewVisitor: NEW_VISITOR,
};
export var buildUserProperties = function (_a) {
    var _b, _c;
    var userInfo = _a.userInfo, userAccount = _a.userAccount, cookies = _a.cookies, rbVisitorID = _a.rbVisitorID, experiments = _a.experiments;
    return {
        event: 'user_properties',
        // biome-ignore lint/complexity/useLiteralKeys: useLiteralKeys
        custom_client_id: (_b = cookies['_ga']) !== null && _b !== void 0 ? _b : '',
        experiment_enrolments: serializeExperiments(experiments) || 'none',
        federated_id: (_c = userInfo.federatedId) !== null && _c !== void 0 ? _c : '',
        joined_date: userAccount && userAccount.joinedAt
            ? formatDate(userAccount.joinedAt)
            : '',
        locale: userInfo.locale,
        onboarded_date: userAccount && userAccount.onboardedAt
            ? formatDate(userAccount.onboardedAt)
            : '',
        user_id: userInfo.userId ? String(userInfo.userId) : '',
        user_type: GaUserTypes.getVisitorType(cookies, userInfo),
        visitor_id_cookie: rbVisitorID,
        custom_country_code: userInfo.country,
    };
};
export var isValidRefList = function (listID) {
    if (Object.keys(AnalyticsItemsListIDNameMap).includes(listID)) {
        return true;
    }
    return false;
};
export var removeAnalyticsStorageKeys = function (urlString) {
    var url = new URL(urlString);
    var searchParams = url.searchParams;
    Object.values(AnalyticsStorageKeys).forEach(function (param) {
        searchParams.delete(param);
    });
    return url;
};
export var parseItemListParams = function (analyticsLocalStorageKeys) {
    var _a;
    var itemListID = analyticsLocalStorageKeys.ref_list_id;
    if (!itemListID)
        return {
            itemListID: null,
            itemIndex: null,
            itemListName: null,
        };
    if (isValidRefList(itemListID)) {
        var searchParamRefListIndex = analyticsLocalStorageKeys.ref_list_index;
        return {
            itemListID: itemListID,
            itemIndex: (_a = Number(searchParamRefListIndex)) !== null && _a !== void 0 ? _a : null,
            itemListName: AnalyticsItemsListIDNameMap[itemListID],
        };
    }
    return {
        itemListID: itemListID,
        itemIndex: null,
        itemListName: null,
    };
};
export var AnalyticsStorage = {
    isValidKey: function (key) {
        return Object.values(AnalyticsStorageKeys).includes(key);
    },
    getValue: function (key) {
        if (!this.isValidKey(key)) {
            var message = "Unable to get analytics local storage value, invalid key provided: \"".concat(key, "\"");
            logger.warn({ message: message, context: 'AnalyticsStorage', key: key }, message);
            return null;
        }
        try {
            // TODO: Check bare localStorage access
            return storage.getItem(key);
        }
        catch (error) {
            logger.warn({ err: error, message: error.message }, "Error getting localStorage for key \"".concat(key, "\":"));
            return null;
        }
    },
    setValue: function (key, value) {
        if (!this.isValidKey(key)) {
            var message = "Unable to get analytics local storage value, invalid key provided: \"".concat(key, "\"");
            logger.warn({ message: message, context: 'AnalyticsStorage', key: key }, message);
            return;
        }
        try {
            // TODO: Check bare localStorage access
            storage.setItem(key, value);
        }
        catch (error) {
            var message = "Error setting value for key \"".concat(key, "\" in localStorage:");
            logger.warn({ err: error, message: message, context: 'AnalyticsStorage', key: key }, message);
        }
    },
    removeItem: function (key) {
        if (!this.isValidKey(key)) {
            var message = "Unable to remove analytics local storage entry, invalid key provided: \"".concat(key, "\"");
            logger.warn({ message: message, context: 'AnalyticsStorage', key: key }, message);
            return;
        }
        try {
            // TODO: Check bare localStorage access
            storage.removeItem(key);
        }
        catch (error) {
            var message = "Error removing entry for key \"".concat(key, "\" in localStorage:");
            logger.warn({ err: error, message: message, context: 'AnalyticsStorage', key: key }, message);
        }
    },
};
export var onSearchCallback = function () {
    AnalyticsStorage.setValue(AnalyticsStorageKeys.RefContextType, AnalyticsContextTypes.SearchBox);
};
export var onTypeaheadResultClick = function (type) {
    switch (type) {
        case constants.TRENDING:
            AnalyticsStorage.setValue(AnalyticsStorageKeys.RefContextType, AnalyticsContextTypes.TrendingTypeahead);
            break;
        case constants.RECENT:
            AnalyticsStorage.setValue(AnalyticsStorageKeys.RefContextType, AnalyticsContextTypes.RecentTypeahead);
            break;
        case constants.POPULAR:
            AnalyticsStorage.setValue(AnalyticsStorageKeys.RefContextType, AnalyticsContextTypes.PopularTypeahead);
            break;
        case constants.ARTISTS:
            AnalyticsStorage.setValue(AnalyticsStorageKeys.RefContextType, AnalyticsContextTypes.ArtistsTypeahead);
            break;
        case constants.CATEGORIES:
            AnalyticsStorage.setValue(AnalyticsStorageKeys.RefContextType, AnalyticsContextTypes.CategoriesTypeahead);
            break;
        case constants.FALLBACK:
            AnalyticsStorage.setValue(AnalyticsStorageKeys.RefContextType, AnalyticsContextTypes.FallbackTypeahead);
            break;
        default:
            break;
    }
};
/**
 * Get topic index from localstorage and reset
 * localstorage variable if it exists
 */
export var getTopicIndex = function () {
    var refTopicIndex = AnalyticsStorage.getValue(AnalyticsStorageKeys.RefContextIndex);
    if (refTopicIndex) {
        AnalyticsStorage.removeItem(AnalyticsStorageKeys.RefContextIndex);
        return refTopicIndex;
    }
    return '';
};
/**
 * Get search type from search paramaters in URL
 * or local storage for typeahead search types
 * typeahead search types are preferenced and if
 * used localstorage is reset
 */
export var getSearchType = function (urlSearchParams, sortOrder) {
    var refTopicType = AnalyticsStorage.getValue(AnalyticsStorageKeys.RefContextType);
    if (refTopicType) {
        AnalyticsStorage.removeItem(AnalyticsStorageKeys.RefContextType);
        return refTopicType;
    }
    if (sortOrder) {
        return AnalyticsContextTypes.Filter;
    }
    return urlSearchParams.get('ref') !== null
        ? String(urlSearchParams.get('ref'))
        : '';
};
/**
 * converts search filters from URLSearchParams to JSON string
 * to be sent to GA4
 */
export var getSearchFilters = function (urlSearchParams) {
    var keyValuePairs = [];
    urlSearchParams.forEach(function (v, k) {
        var _a;
        keyValuePairs.push((_a = {}, _a[k] = v, _a));
    });
    return JSON.stringify(keyValuePairs);
};
/**
 * Get topic index from localstorage and reset
 * localstorage variable if it exists
 */
export var getTopicClickedLabel = function () {
    var refContextLabel = AnalyticsStorage.getValue(AnalyticsStorageKeys.RefContextLabel);
    if (refContextLabel) {
        AnalyticsStorage.removeItem(AnalyticsStorageKeys.RefContextLabel);
        return refContextLabel;
    }
    return '';
};
